import { useEffect, useMemo } from 'react';
import styled from 'styled-components';

import { Flex, Space, VFlex } from '@/components/base';
import Spinner from '@/components/Common/Spinner';
import { MainText } from '@/components/Typography/NamedTypo';
import Typo from '@/components/Typography/Typo';
import useColor from '@/hooks/domain/useColor';
import useStates from '@/hooks/pure/useStates';
import type { DetailModalOptions } from '@/stores/pure/DetailModalStore';
import type { CATEGORY_TYPE } from '@/types/categoryConst';
import { FEEDBACK_BODY_TYPE, type FeedbackSentiment } from '@/types/feedbackConst';

import Attachment from '../Attachment';
import ChatMessageDisplay from './BodyDisplay/ChatMessageDisplay';
import { DiscussionDisplay } from './BodyDisplay/DiscussionDisplay';
import { RawTextDisplay } from './BodyDisplay/RawTextDisplay';
import { SurveyDisplay } from './BodyDisplay/SurveyDisplay';
import { ThreadDisplay } from './BodyDisplay/ThreadDisplay';

export type BlockFilterState = {
  sentiment: FeedbackSentiment | null;
  category: CATEGORY_TYPE | null;
  topic: string | null;
};

type Props = {
  feedbackAlias: string;
  detail?: FeedbackT | null;
  chatText: ChatMessageT[];
  chatTitle: string;
  feedbackDetailFullView: boolean;
  options?: DetailModalOptions<{
    sourceId?: string;
    focusedBlock?: string;
  }>;
};

const initialStateValue: BlockFilterState = {
  sentiment: null,
  category: null,
  topic: null,
};

const FeedbackDetailBodyArea = (props: Props) => {
  const blockFilterStates = useStates<BlockFilterState>(initialStateValue);
  const color = useColor();

  const filteredBlock = useMemo(() => {
    const targetFilter = Object.keys(blockFilterStates.state).find(
      (key) =>
        blockFilterStates.state[key as keyof typeof blockFilterStates.state] !==
        null,
    );
    if (!targetFilter) return props.detail?.blocks || [];
    return (
      props.detail?.blocks?.filter(
        (block) =>
          block[targetFilter as keyof typeof block] ===
          blockFilterStates.state[
            targetFilter as keyof typeof blockFilterStates.state
          ],
      ) || []
    );
  }, [blockFilterStates, props.detail?.blocks]);

  const bodyRender = useMemo(() => {
    if (!props.detail) return null;
    switch (props.detail?.body_type) {
      case FEEDBACK_BODY_TYPE.SURVEY:
        return (
          <SurveyDisplay
            detail={props.detail}
            focusedBlock={props.options?.focusedBlock}
          />
        );
      case FEEDBACK_BODY_TYPE.DISCUSSION:
        return (
          <DiscussionDisplay
            detail={{
              ...props.detail,
              blocks: filteredBlock,
            }}
            focusedBlock={props.options?.focusedBlock}
          />
        );
      case FEEDBACK_BODY_TYPE.THREAD:
        return (
          <ThreadDisplay
            detail={{
              ...props.detail,
              blocks: filteredBlock,
            }}
            feedbackDetailFullView={props.feedbackDetailFullView}
            feedbackAlias={props.feedbackAlias}
            focusedBlock={props.options?.focusedBlock}
          />
        );
      case FEEDBACK_BODY_TYPE.CHAT: {
        return (
          <ChatMessageDisplay
            feedbackDetailFullView={props.feedbackDetailFullView}
            feedbackAlias={props.feedbackAlias}
            detail={{
              ...props.detail,
              blocks: filteredBlock,
            }}
            focusedBlock={props.options?.focusedBlock}
          />
        );
      }
      case FEEDBACK_BODY_TYPE.PLAIN_TEXT:
        return (
          <RawTextDisplay
            detail={{
              ...props.detail,
              blocks: filteredBlock,
            }}
            focusedBlock={props.options?.focusedBlock}
          />
        );
      default: {
        console.error(
          'Unsupported feedback body type',
          props.detail?.body_type,
        );
        return (
          <RawTextDisplay
            detail={{
              ...props.detail,
              blocks: filteredBlock,
            }}
            focusedBlock={props.options?.focusedBlock}
          />
        );
      }
    }
  }, [
    filteredBlock,
    props.detail,
    props.feedbackAlias,
    props.feedbackDetailFullView,
    props.options?.focusedBlock,
  ]);

  useEffect(() => {
    const target = document.querySelector('.block-focused');
    target?.scrollIntoView({
      block: 'center',
    });
  }, [bodyRender]);

  return (
    <VFlex g={16}>
      {props.detail?.desc && (
        <DescriptionArea g={8}>
          <MainText dim>Description</MainText>
          <Typo type="body2" weight="m">
            {props.detail.desc}
          </Typo>
        </DescriptionArea>
      )}
      {props.detail ? (
        bodyRender
      ) : (
        <Flex a="center" j="center" style={{ height: 250 }}>
          <Spinner />
        </Flex>
      )}
      {props.detail?.attachments && (
        <VFlex className="pt-2">
          <Typo
            type="label3"
            weight="sb"
            color={color.gray600}
            style={{ height: '20px' }}
          >
            Attachment
          </Typo>
          <Space h="4px" />
          <Attachment
            key={props.detail.alias}
            attachments={props.detail.attachments}
          />
        </VFlex>
      )}
    </VFlex>
  );
};

const DescriptionArea = styled(VFlex)`
  padding: 12px 16px;
  border: 1px solid ${(props) => props.theme.colors.gray400};
  border-radius: 8px;
`;

export default FeedbackDetailBodyArea;
